// src/context/CreditContext.js
// ---------------------------------------------------------------------------
// Phase 6 (FINAL) — global credit-usage context for the chat-area alert.
//
// Provides the authenticated app shell with the read-only usage state needed
// by the chat-area credit alert and the Send-disabled-at-100% behavior.
// Mounted inside AuthProvider (so it can react to auth state).
//
// Responsibilities:
//   * fetch usage on auth load;
//   * refresh on window focus (returns from /billing / hosted checkout) —
//     authoritative state drives the alert + Send enablement;
//   * clear local credit state on logout.
//
// There is deliberately NO dialog, NO toast/notification wiring, NO header
// indicator, and NO threshold state machine in this context.
// ---------------------------------------------------------------------------
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { useCreditUsage } from '../hooks/useCreditUsage';

const CreditContext = createContext(null);

export function CreditProvider({ children }) {
  const { user } = useAuth();

  const credit = useCreditUsage({});

  const { refresh, resetForLogout } = credit;

  // Fetch on auth load (user present).
  useEffect(() => {
    if (user) {
      refresh(true);
    }
  }, [user, refresh]);

  // Clear local credit state on logout. The limit/usage state itself is NOT
  // reset — it re-hydrates from the server on the next authenticated load.
  useEffect(() => {
    if (!user) {
      resetForLogout();
    }
  }, [user, resetForLogout]);

  // Refresh on window focus. Returning from /billing (or a hosted Stripe
  // checkout) always re-reads the authoritative state, so the exhausted alert
  // disappears and Send is re-enabled ONLY when the server actually reports
  // usable credit. Focus events are infrequent (tab switches) — this is not
  // polling.
  useEffect(() => {
    const onFocus = () => {
      if (user) refresh(true);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, refresh]);

  const value = useMemo(() => ({ ...credit }), [credit]);

  return <CreditContext.Provider value={value}>{children}</CreditContext.Provider>;
}

export function useCreditUsageContext() {
  return useContext(CreditContext);
}
